import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {Dimensions, StyleSheet, Text, View} from 'react-native';
import {PieChart} from 'react-native-chart-kit';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import {useGetDashboardsQuery} from '../../redux/services/dashboardApi';
import {RootState} from '../../redux/store';
import {
  colors,
  spacing,
  shadows,
  borderRadius,
  typography,
} from '../../theme/theme';

import T from '../../Components/T';
const AccountBalance = ({date}: {date: Date}) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const [totSales, setTotSales] = useState(0);
  const [totExp, setTotExp] = useState(0);
  const [totPur, setTotPur] = useState(0);
  const [FO, setFO] = useState(50);
  const [POS, setPOS] = useState(40);
  const [HALL, setHALL] = useState(5);
  const [OTH, setOTH] = useState(5);
  const {data} = useGetDashboardsQuery({
    cid: gdata.company,
    dt: moment(date).utcOffset('+05:30').format(),
  });

  useEffect(() => {
    let totSales1 = 0;
    let FO1 = 0,
      POS1 = 0,
      HALL1 = 0,
      OTH1 = 0,
      EXP1 = 0,
      PUR1 = 0;
    data?.[0]?.forEach((v: any) => {
      if (v.db_name === '1. FO') FO1 += Number(v.db_amt);
      else if (v.db_name === '2. POS') POS1 += Number(v.db_amt);
      else if (v.db_name === '3. HALL') HALL1 += Number(v.db_amt);
      else if (v.db_name === '4. HK') OTH1 += Number(v.db_amt);
      else if (v.db_name === '5. OTH') OTH1 += Number(v.db_amt);
      else if (v.db_name === '6. Pur') PUR1 += Number(v.db_amt);
      else EXP1 += Number(v.db_amt);
    });
    totSales1 += FO1 + POS1 + HALL1 + OTH1;
    setTotSales(totSales1);
    setTotExp(EXP1);
    setTotPur(PUR1);
    setFO(FO1 || 1);
    setPOS(POS1 || 1);
    setHALL(HALL1 || 1);
    setOTH(OTH1 || 1);
  }, [data]);

  const screenWidth = Dimensions.get('window').width;

  const chartData = [
    {
      name: 'FO',
      population: FO,
      color: '#667eea',
      legendFontColor: colors.textPrimary,
      legendFontSize: 13,
    },
    {
      name: 'POS',
      population: POS,
      color: '#38bdf8',
      legendFontColor: colors.textPrimary,
      legendFontSize: 13,
    },
    {
      name: 'HALL',
      population: HALL,
      color: '#a855f7',
      legendFontColor: colors.textPrimary,
      legendFontSize: 13,
    },
    {
      name: 'OTH',
      population: OTH,
      color: '#f59e0b',
      legendFontColor: colors.textPrimary,
      legendFontSize: 13,
    },
  ];

  return (
    <View style={styles.container}>
      {/* Overview Metric Hero Cards */}
      <View style={styles.metricsGrid}>
        {/* Sales Hero Card */}
        <View style={[styles.kpiCard, styles.salesCard]}>
          <View style={styles.kpiHeader}>
            <View style={[styles.iconBadge, {backgroundColor: colors.successLight}]}>
              <FontAwesome name="line-chart" size={18} color={colors.success} />
            </View>
            <Text style={styles.kpiTitle}><T>Today's Sales</T></Text>
          </View>
          <Text style={[styles.kpiValue, {color: colors.success}]}>
            ₹{totSales.toLocaleString()}
          </Text>
        </View>

        {/* Purchases Hero Card */}
        <View style={[styles.kpiCard, styles.purchasesCard]}>
          <View style={styles.kpiHeader}>
            <View style={[styles.iconBadge, {backgroundColor: colors.warningLight}]}>
              <FontAwesome name="shopping-cart" size={18} color={colors.warning} />
            </View>
            <Text style={styles.kpiTitle}><T>Today's Purchases</T></Text>
          </View>
          <Text style={[styles.kpiValue, {color: colors.warning}]}>
            ₹{totPur.toLocaleString()}
          </Text>
        </View>

        {/* Expenses Hero Card */}
        <View style={[styles.kpiCard, styles.expensesCard]}>
          <View style={styles.kpiHeader}>
            <View style={[styles.iconBadge, {backgroundColor: colors.errorLight}]}>
              <FontAwesome name="credit-card" size={18} color={colors.error} />
            </View>
            <Text style={styles.kpiTitle}><T>Today's Expenses</T></Text>
          </View>
          <Text style={[styles.kpiValue, {color: colors.error}]}>
            ₹{totExp.toLocaleString()}
          </Text>
        </View>
      </View>

      {/* Chart & Department Breakdown Card */}
      <View style={styles.chartCard}>
        <View style={styles.cardHeaderRow}>
          <FontAwesome name="pie-chart" size={20} color={colors.accent} style={{marginRight: 8}} />
          <Text style={styles.cardHeaderTitle}><T>Sales Distribution</T></Text>
        </View>

        <View style={styles.pieContainer}>
          <PieChart
            data={chartData}
            width={Math.min(screenWidth - 60, 360)}
            height={200}
            chartConfig={{
              backgroundColor: colors.white,
              backgroundGradientFrom: colors.white,
              backgroundGradientTo: colors.white,
              color: (opacity = 1) => `rgba(102, 126, 234, ${opacity})`,
            }}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="15"
            absolute={false}
          />
        </View>

        {/* Mini Breakdown Stat Badges */}
        <View style={styles.breakdownGrid}>
          <View style={styles.breakdownItem}>
            <View style={[styles.avatarBadge, {backgroundColor: '#667eea'}]}>
              <FontAwesome name="building-o" size={16} color={colors.white} />
            </View>
            <Text style={styles.breakdownLabel}><T>Front Office</T></Text>
            <Text style={styles.breakdownAmount}>₹{FO.toLocaleString()}</Text>
          </View>

          <View style={styles.breakdownItem}>
            <View style={[styles.avatarBadge, {backgroundColor: '#38bdf8'}]}>
              <FontAwesome name="cutlery" size={16} color={colors.white} />
            </View>
            <Text style={styles.breakdownLabel}><T>POS Sales</T></Text>
            <Text style={styles.breakdownAmount}>₹{POS.toLocaleString()}</Text>
          </View>

          <View style={styles.breakdownItem}>
            <View style={[styles.avatarBadge, {backgroundColor: '#a855f7'}]}>
              <FontAwesome name="glass" size={16} color={colors.white} />
            </View>
            <Text style={styles.breakdownLabel}><T>Banquet/Hall</T></Text>
            <Text style={styles.breakdownAmount}>₹{HALL.toLocaleString()}</Text>
          </View>

          <View style={styles.breakdownItem}>
            <View style={[styles.avatarBadge, {backgroundColor: '#f59e0b'}]}>
              <FontAwesome name="asterisk" size={16} color={colors.white} />
            </View>
            <Text style={styles.breakdownLabel}><T>Others</T></Text>
            <Text style={styles.breakdownAmount}>₹{OTH.toLocaleString()}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: spacing.xs,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  kpiCard: {
    flex: 1,
    minWidth: 150,
    backgroundColor: colors.white,
    borderRadius: borderRadius.xl,
    padding: spacing.md,
    ...shadows.md,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  salesCard: {
    borderLeftWidth: 4,
    borderLeftColor: colors.success,
  },
  purchasesCard: {
    borderLeftWidth: 4,
    borderLeftColor: colors.warning,
  },
  expensesCard: {
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
  },
  kpiHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  iconBadge: {
    width: 32,
    height: 32,
    borderRadius: borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  kpiTitle: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textSecondary,
  },
  kpiValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.xs,
  },
  chartCard: {
    backgroundColor: colors.white,
    borderRadius: borderRadius['2xl'],
    padding: spacing.lg,
    marginVertical: spacing.xs,
    ...shadows.lg,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  cardHeaderTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  pieContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: spacing.xs,
  },
  breakdownGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    gap: spacing.sm,
  },
  breakdownItem: {
    alignItems: 'center',
    width: '22%',
    minWidth: 70,
  },
  avatarBadge: {
    width: 42,
    height: 42,
    borderRadius: 21,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.xs,
    ...shadows.sm,
  },
  breakdownLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium,
    textAlign: 'center',
  },
  breakdownAmount: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    marginTop: 2,
  },
});

export default AccountBalance;
